import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

// Options pour les filtres
const STATUT_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "publie", label: "Publié" },
  { value: "brouillon", label: "Brouillon" },
  { value: "archive", label: "Archivé" }
];

const ACTIF_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "1", label: "Actif" },
  { value: "0", label: "Inactif" }
];

const CATEGORIE_OPTIONS = [
  { value: "", label: "Toutes catégories" },
  { value: "securite", label: "Sécurité" },
  { value: "formation", label: "Formation" },
  { value: "procedure", label: "Procédure" },
  { value: "technique", label: "Technique" }
];

const statutLabel = (statut) => {
  switch (statut) {
    case "publie":
      return { 
        text: "Publié", 
        icon: "bi-eye",
        color: COLORS.success
      };
    case "brouillon":
      return { 
        text: "Brouillon", 
        icon: "bi-file-earmark",
        color: COLORS.warning
      };
    case "archive":
      return { 
        text: "Archivé", 
        icon: "bi-archive",
        color: "#757575"
      };
    default:
      return { 
        text: "Inconnu", 
        icon: "bi-question-circle",
        color: COLORS.darkText
      };
  }
};

const actifLabel = (actif) => {
  return actif === 1 ? {
    text: "Actif",
    icon: "bi-check-circle",
    color: COLORS.success
  } : {
    text: "Inactif",
    icon: "bi-x-circle",
    color: COLORS.danger
  };
};

const getCategoryLabel = (categorie) => {
  const found = CATEGORIE_OPTIONS.find(opt => opt.value === categorie);
  return found ? found.label : categorie;
};

const getCategoryIcon = (categorie) => {
  switch(categorie) {
    case "securite": return "bi-shield-check";
    case "formation": return "bi-mortarboard";
    case "procedure": return "bi-clipboard-check";
    case "technique": return "bi-tools";
    default: return "bi-film";
  }
};

const getCategoryColor = (categorie) => {
  switch(categorie) {
    case "securite": return "#d32f2f";
    case "formation": return "#1976d2";
    case "procedure": return "#388e3c";
    case "technique": return "#f57c00";
    default: return "#757575";
  }
};

const formatDuration = (seconds) => {
  if (!seconds) return "00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

// Composant VideoPlayer pour chaque tutoriel
const VideoPlayer = ({ videoUrl, duration, videoId, onPlay, onPause, isPlaying, categoryColor }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Mettre à jour le temps courant
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', updateTime);
    return () => video.removeEventListener('timeupdate', updateTime);
  }, []);

  // Gérer les erreurs de chargement
  const handleError = () => {
    console.error(`Erreur de chargement de la vidéo: ${videoUrl}`);
    setHasError(true);
  };

  // Calculer le pourcentage de progression
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Formater le temps actuel
  const formatCurrentTime = () => {
    return formatDuration(currentTime);
  };

  // Basculer play/pause
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => {
        onPlay(videoId);
      }).catch(error => {
        console.error("Erreur de lecture:", error);
        setHasError(true);
      });
    } else {
      video.pause();
      onPause();
    }
  };

  // Changer le volume
  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // Changer la position de la vidéo
  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const newTime = percent * duration;

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  if (hasError) {
    return (
      <div className="video-error-container" style={{
        backgroundColor: '#1a1a1a',
        aspectRatio: '16/9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff'
      }}>
        <i className="bi bi-exclamation-triangle fs-1 mb-3" style={{ color: '#ff4444' }}></i>
        <span className="mb-2">Erreur de chargement de la vidéo</span>
        <small className="text-muted">URL: {videoUrl?.substring(0, 50)}...</small>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="video-player-container"
      style={{
        position: 'relative',
        backgroundColor: '#000',
        aspectRatio: '16/9',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onMouseEnter={() => {
        setIsHovering(true);
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        if (!isPlaying) {
          setShowControls(false);
        }
      }}
      onClick={(e) => {
        // Ne pas déclencher le play si on clique sur les contrôles
        if (!e.target.closest('.video-controls')) {
          togglePlay();
        }
      }}
    >
      {/* Élément vidéo */}
      <video
        ref={videoRef}
        src={videoUrl}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000'
        }}
        preload="metadata"
        onError={handleError}
        playsInline
      />

      {/* Overlay de play/pause */}
      {!isPlaying && (
        <div 
          className="video-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            transition: 'background-color 0.3s ease'
          }}
        >
          <div 
            className="play-button"
            style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: categoryColor,
              fontSize: '2rem',
              transition: 'all 0.3s ease',
              transform: isHovering ? 'scale(1.1)' : 'scale(1)'
            }}
          >
            <i className="bi bi-play-fill"></i>
          </div>
        </div>
      )}

      {/* Contrôles vidéo (toujours visibles pendant la lecture ou au survol) */}
      {(isPlaying || showControls || isHovering) && (
        <div 
          className="video-controls"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            padding: '10px',
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            color: '#fff',
            transition: 'opacity 0.3s ease',
            opacity: isHovering || isPlaying ? 1 : 0
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Barre de progression */}
          <div 
            className="progress-bar-container"
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
              marginBottom: '10px',
              cursor: 'pointer'
            }}
            onClick={handleProgressClick}
          >
            <div 
              className="progress-bar"
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: categoryColor,
                borderRadius: '2px',
                transition: 'width 0.1s linear'
              }}
            />
          </div>

          {/* Contrôles inférieurs */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Bouton play/pause */}
              <button
                onClick={togglePlay}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
              </button>

              {/* Bouton mute/unmute */}
              <button
                onClick={toggleMute}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
              </button>

              {/* Temps */}
              <span style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>
                {formatCurrentTime()} / {formatDuration(duration)}
              </span>
            </div>

            {/* Indicateur de lecture */}
            {isPlaying && (
              <span style={{
                fontSize: '0.8rem',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>
                <i className="bi bi-play-circle me-1"></i>
                En lecture
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal
const TutorialAdminList = () => {
  const navigate = useNavigate();
  
  const [tutos, setTutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("");
  const [filterActif, setFilterActif] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Statistiques
  const [statistiques, setStatistiques] = useState({});

  const handleFetchError = useCallback((err) => {
    console.error("Fetch error details:", {
      message: err.message,
      response: err.response?.data,
      status: err.response?.status
    });
    
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Administrateur requis.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur: " + (err.response.data?.message || "Veuillez contacter l'administrateur."));
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion réseau.");
    } else {
      setServerError(`Erreur: ${err.message}`);
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    console.log("fetchData called, currentProfile:", currentProfile);
    
    if (currentProfile !== "1") {
      console.log("Not admin, skipping fetch");
      return;
    }
    
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Construire les paramètres de recherche pour l'API
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      if (filterStatut) params.append("statut", filterStatut);
      if (filterCategorie) params.append("categorie", filterCategorie);
      if (filterActif !== "") params.append("actif", filterActif);
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      
      const url = `${API_BASE_URL}/tuto/list_admin.php?${params.toString()}`;
      
      console.log("Fetching data from:", url);
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });
      
      console.log("API Response received:", response.data);
      
      if (response.data?.success) {
        const tutosData = response.data.data.tutos || [];
        console.log(`Received ${tutosData.length} tutos`);
        setTutos(tutosData);
        
        // Mettre à jour la pagination
        const pagination = response.data.data.pagination || {};
        setTotalPages(pagination.total_pages || 1);
        setTotalItems(pagination.total || 0);
        
        // Mettre à jour les statistiques
        setStatistiques(response.data.data.statistiques || {});
        
        if (tutosData.length === 0) {
          if (searchTerm || filterStatut || filterCategorie || filterActif !== "") {
            setInfoMsg("Aucun tutoriel ne correspond aux critères de recherche.");
          } else {
            setInfoMsg("Aucun tutoriel trouvé dans la base de données.");
          }
        }
      } else {
        setServerError(response.data?.message || "Impossible de charger la liste des tutoriels.");
      }
    } catch (err) {
      console.error("API Call Error:", err);
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [
    currentProfile, 
    searchTerm, 
    filterStatut, 
    filterCategorie, 
    filterActif, 
    currentPage, 
    itemsPerPage, 
    navigate, 
    handleFetchError
  ]);

  // Initialisation et vérification du profil
  useEffect(() => {
    console.log("TutorialAdminList component mounted");
    
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setServerError("Session non trouvée. Veuillez vous reconnecter.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    try {
      const userData = JSON.parse(storedUser);
      const profile = String(userData.profile || "").trim();
      console.log("User profile:", profile);
      setCurrentProfile(profile);
      
      if (profile !== "1") {
        setServerError("Accès réservé aux administrateurs uniquement");
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("Error parsing user:", error);
      setServerError("Erreur de session utilisateur");
      setLoading(false);
    }
  }, [navigate]);

  // Appeler fetchData quand les filtres ou la pagination changent
  useEffect(() => {
    console.log("useEffect triggered, currentProfile:", currentProfile);
    if (currentProfile === "1") {
      console.log("Current profile is admin, fetching data...");
      fetchData();
    }
  }, [currentProfile, fetchData]);

  // Gérer le play/pause des vidéos
  const handleVideoPlay = (videoId) => {
    // Arrêter toute autre vidéo en cours
    if (playingVideoId && playingVideoId !== videoId) {
      // On pourrait ajouter une logique pour arrêter l'autre vidéo
    }
    setPlayingVideoId(videoId);
  };

  const handleVideoPause = () => {
    setPlayingVideoId(null);
  };

  const handleViewDetails = (tuto) => {
    console.log("View details for tuto:", tuto.id_video);
    navigate(`/tutos/details/${tuto.id_video}`);
  };

  const handleCreateTuto = () => {
    navigate("/tutos/create");
  };

  const handleEditTuto = (tuto) => {
    navigate(`/tutos/edit/${tuto.id_video}`);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatut("");
    setFilterCategorie("");
    setFilterActif("");
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    return searchTerm || filterStatut || filterCategorie || filterActif !== "";
  };

  // Calculer les statistiques
  const getStatistiquesData = () => {
    const stats = {
      total: 0,
      publie: { total: 0, actifs: 0 },
      brouillon: { total: 0, actifs: 0 },
      archive: { total: 0, actifs: 0 }
    };

    Object.entries(statistiques).forEach(([statut, data]) => {
      stats[statut] = data;
      stats.total += data.total;
    });

    return stats;
  };

  const statsData = getStatistiquesData();

  // Pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Si pas admin, afficher message d'accès restreint
  if (currentProfile && currentProfile !== "1") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des tutoriels</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tutoriels</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-lock fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Vous n'avez pas les permissions nécessaires pour accéder à cette page. Cette fonctionnalité est réservée aux administrateurs.
                </p>
                
                <button
                  className="btn fw-semibold mt-4"
                  onClick={() => navigate("/dashboard")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des tutoriels vidéo</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tutoriels</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-collection-play"></i>
                    </div>
                    Catalogue des tutoriels
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les tutoriels vidéos de formation et procédures
                </p>
              </div>
              
              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={handleResetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
               
                <button
                  className="btn fw-semibold"
                  onClick={handleCreateTuto}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Nouveau tutoriel
                </button>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && !loading && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Cartes de statistiques */}
            <div className="row mb-4">
              <div className="col-xl-3 col-md-6">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-white mb-1" style={{ fontWeight: '500', opacity: 0.9 }}>Total tutoriels</h6>
                        <h2 className="text-white mb-0">{statsData.total}</h2>
                        <small className="text-white" style={{ opacity: 0.8, fontSize: '0.85rem' }}>Tous statuts confondus</small>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: 'rgba(255,255,255,0.2)'
                      }}>
                        <i className="bi bi-collection-play fs-4 text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  backgroundColor: COLORS.white
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontWeight: '500' }}>Publiés</h6>
                        <h2 className="mb-0" style={{ color: COLORS.success }}>{statsData.publie?.total || 0}</h2>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {statsData.publie?.actifs || 0} actifs
                        </small>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: COLORS.successLight
                      }}>
                        <i className="bi bi-eye fs-4" style={{ color: COLORS.success }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  backgroundColor: COLORS.white
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontWeight: '500' }}>Brouillons</h6>
                        <h2 className="mb-0" style={{ color: COLORS.warning }}>{statsData.brouillon?.total || 0}</h2>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {statsData.brouillon?.actifs || 0} actifs
                        </small>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: COLORS.warningLight
                      }}>
                        <i className="bi bi-file-earmark fs-4" style={{ color: COLORS.warning }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  backgroundColor: COLORS.white
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontWeight: '500' }}>Archivés</h6>
                        <h2 className="mb-0" style={{ color: '#757575' }}>{statsData.archive?.total || 0}</h2>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {statsData.archive?.actifs || 0} actifs
                        </small>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#f5f5f5'
                      }}>
                        <i className="bi bi-archive fs-4" style={{ color: '#757575' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher (titre, description)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                          {searchTerm && (
                            <button
                              className="btn"
                              type="button"
                              onClick={handleResetSearch}
                              title="Réinitialiser la recherche"
                              style={{
                                color: COLORS.primary,
                                borderColor: '#e0e0e0',
                                backgroundColor: COLORS.white,
                                borderLeft: 'none'
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {STATUT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-tags"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterCategorie}
                            onChange={(e) => setFilterCategorie(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {CATEGORIE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-power"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterActif}
                            onChange={(e) => setFilterActif(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {ACTIF_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <button
                          className="btn fw-semibold w-100"
                          onClick={handleApplyFilters}
                          style={{
                            color: COLORS.white,
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            borderRadius: '8px',
                            padding: '10px'
                          }}
                        >
                          <i className="bi bi-funnel-fill me-2"></i>
                          Appliquer
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {totalItems} tutoriel(s) au total
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {searchTerm && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                            {filterStatut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Statut</span>}
                            {filterCategorie && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Catégorie</span>}
                            {filterActif !== "" && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Actif/Inactif</span>}
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={fetchData}
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          fontSize: '0.85rem'
                        }}
                      >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''} me-1`}></i>
                        {loading ? 'Chargement...' : 'Actualiser'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des tutoriels...</p>
              </div>
            ) : tutos.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-collection-play fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun tutoriel trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  {infoMsg || "Aucun tutoriel n'a été créé pour le moment."}
                </p>
                <button
                  onClick={handleCreateTuto}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Créer un tutoriel
                </button>
              </div>
            ) : (
              <>
                <div className="row g-4">
                  {tutos.map((tuto) => {
                    const status = statutLabel(tuto.statut_video);
                    const actif = actifLabel(tuto.actif_video);
                    const categoryColor = getCategoryColor(tuto.categorie_video);
                    const categoryIcon = getCategoryIcon(tuto.categorie_video);
                    
                    const isInactive = !tuto.actif_video;
                    const videoId = `video-${tuto.id_video}`;
                    const isPlaying = playingVideoId === videoId;

                    return (
                      <div key={tuto.id_video} className="col-lg-6">
                        <div className="card h-100" style={{ 
                          border: `1px solid ${isInactive ? '#ffcdd2' : '#e0e0e0'}`,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          opacity: isInactive ? 0.85 : 1,
                          backgroundColor: isInactive ? '#fff8f8' : COLORS.white
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}>
                          {/* En-tête avec titre et badges */}
                          <div className="card-header border-0 pb-0" style={{ 
                            backgroundColor: isInactive ? '#B0BEC5' : categoryColor,
                            color: COLORS.white,
                            padding: '16px 20px'
                          }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <h5 className="card-title mb-2" style={{ 
                                  color: COLORS.white,
                                  fontSize: '1rem',
                                  fontWeight: '600',
                                  lineHeight: '1.3'
                                }}>
                                  {tuto.titre_video}
                                </h5>
                                <div className="d-flex align-items-center">
                                  <i className={`bi ${categoryIcon} me-2`}></i>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: COLORS.white,
                                    fontSize: '0.75rem',
                                    padding: '4px 8px',
                                    borderRadius: '4px'
                                  }}>
                                    {getCategoryLabel(tuto.categorie_video)}
                                  </span>
                                </div>
                              </div>
                              
                              {isInactive && (
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: COLORS.warning,
                                  color: '#212529',
                                  fontSize: '0.7rem',
                                  padding: '4px 8px',
                                  borderRadius: '4px'
                                }}>
                                  <i className="bi bi-eye-slash me-1"></i>
                                  Inactif
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Section vidéo avec le lecteur intégré */}
                          <VideoPlayer
                            videoUrl={tuto.video_url_complete}
                            duration={tuto.duree_video}
                            videoId={videoId}
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                            isPlaying={isPlaying}
                            categoryColor={categoryColor}
                          />
                          
                          {/* Corps de la carte */}
                          <div className="card-body">
                            <div className="mb-3">
                              <p className="card-text" style={{ 
                                color: COLORS.darkText,
                                fontSize: '0.9rem',
                                minHeight: '60px'
                              }}>
                                {tuto.description_video || "Pas de description disponible."}
                              </p>
                            </div>
                            
                            <div className="row mb-3">
                              <div className="col-6">
                                <div className="text-center p-2 rounded" style={{ 
                                  backgroundColor: '#E3F2FD',
                                  border: `1px solid ${COLORS.secondary}20`
                                }}>
                                  <div className="d-flex align-items-center justify-content-center">
                                    <i className={`bi ${status.icon} me-2`} style={{ color: status.color }}></i>
                                    <span className="small fw-bold" style={{ color: COLORS.primary }}>
                                      {status.text}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="col-6">
                                <div className="text-center p-2 rounded" style={{ 
                                  backgroundColor: actif.color === COLORS.success ? '#E8F5E9' : '#FFEBEE',
                                  border: `1px solid ${actif.color}20`
                                }}>
                                  <div className="d-flex align-items-center justify-content-center">
                                    <i className={`bi ${actif.icon} me-2`} style={{ color: actif.color }}></i>
                                    <span className="small fw-bold" style={{ color: actif.color }}>
                                      {actif.text}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDateTime(tuto.date_publication || tuto.created_at)}
                              </small>
                              <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <i className="bi bi-clock me-1"></i>
                                {formatDuration(tuto.duree_video)}
                              </small>
                            </div>
                          </div>
                          
                          {/* Pied de la carte */}
                          <div className="card-footer border-0 pt-0 pb-3 px-3">
                            <div className="d-flex gap-2 justify-content-between">
                              <button
                                className="btn fw-semibold"
                                onClick={() => handleViewDetails(tuto)}
                                title="Voir les détails"
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  flex: 1,
                                  transition: 'all 0.3s ease',
                                  fontSize: '0.9rem'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = COLORS.primary;
                                  e.target.style.color = COLORS.white;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = COLORS.primary;
                                }}
                              >
                                <i className="bi bi-eye me-2"></i>
                                Détails complets
                              </button>
                              
                              <button
                                className="btn fw-semibold"
                                onClick={() => handleEditTuto(tuto)}
                                title="Modifier"
                                style={{
                                  color: COLORS.success,
                                  borderColor: COLORS.success,
                                  backgroundColor: 'transparent',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  width: '48px',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = COLORS.success;
                                  e.target.style.color = COLORS.white;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = COLORS.success;
                                }}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                      <select 
                        className="form-select form-select-sm w-auto"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText,
                          borderRadius: '6px'
                        }}
                      >
                        <option value="8">8</option>
                        <option value="16">16</option>
                        <option value="24">24</option>
                        <option value="32">32</option>
                      </select>
                      <span className="ms-3" style={{ color: COLORS.darkText }}>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {totalItems} tutoriel(s) au total
                        </span>
                        <span className="text-muted small">
                          Page {currentPage} sur {totalPages}
                        </span>
                      </span>
                    </div>
                    
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            style={{
                              color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                              borderColor: '#e0e0e0',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            if (totalPages <= 7) return true;
                            return page === 1 || 
                                   page === totalPages || 
                                   (page >= currentPage - 1 && page <= currentPage + 1);
                          })
                          .map((page, index, array) => {
                            if (index > 0 && page > array[index - 1] + 1) {
                              return (
                                <React.Fragment key={`ellipsis-${page}`}>
                                  <li className="page-item disabled">
                                    <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                  </li>
                                  <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px'
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                </React.Fragment>
                              );
                            }
                            
                            return (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link fw-semibold"
                                  onClick={() => handlePageChange(page)}
                                  style={{
                                    backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                    borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                    color: currentPage === page ? COLORS.white : COLORS.primary,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    margin: '0 2px'
                                  }}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          })}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            style={{
                              color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                              borderColor: '#e0e0e0',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .bi-arrow-clockwise.spin {
          animation: spin 1s linear infinite;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
          
          .col-lg-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
        
        @media (max-width: 576px) {
          .card-header h5 {
            font-size: 0.95rem;
          }
          
          .card-body .row .col-6 {
            padding: 0 4px;
          }
          
          .card-footer .btn {
            font-size: 0.8rem !important;
            padding: 6px 8px !important;
          }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Layout>
  );
};

export default TutorialAdminList;